import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { X, LoaderCircle } from "lucide-react";
import { parseMoney } from "../../shared/domain";
export function Spinner() {
  return <LoaderCircle className="spinner" size={18} aria-hidden="true" />;
}
export function Field({
  label,
  hint,
  error,
  children,
  required = false,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <label className={`field ${error ? "invalid" : ""}`}>
      <span>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </span>
      {children}
      {error ? (
        <small className="field-error">{error}</small>
      ) : hint ? (
        <small>{hint}</small>
      ) : null}
    </label>
  );
}
export function AmountInput({
  value,
  onChange,
  id,
  required = false,
}: {
  value: number;
  onChange: (n: number) => void;
  id?: string;
  required?: boolean;
}) {
  const [text, setText] = useState(value ? (value / 100).toFixed(2) : "");
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value ? (value / 100).toFixed(2) : "");
  }, [value]);
  return (
    <span className="money-input">
      <span aria-hidden="true">£</span>
      <input
        id={id}
        inputMode="decimal"
        type="text"
        autoComplete="off"
        value={text}
        required={required}
        pattern="[0-9]{1,7}(\.[0-9]{0,2})?"
        placeholder="0.00"
        onFocus={() => (focused.current = true)}
        onBlur={() => {
          focused.current = false;
          const n = parseMoney(text);
          if (n !== null) setText((n / 100).toFixed(2));
        }}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseMoney(e.target.value) ?? 0);
        }}
      />
    </span>
  );
}
export function Modal({
  title,
  children,
  onClose,
  sheet = false,
  dismissible = true,
  returnFocus,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  sheet?: boolean;
  dismissible?: boolean;
  returnFocus?: { current: HTMLElement | null };
}) {
  const ref = useRef<HTMLDialogElement>(null),
    id = useId();
  const closing = useRef(false);
  const drag = useRef<number | null>(null);
  const dragged = useRef(false);
  const close = () => {
    if (!dismissible || closing.current) return;
    closing.current = true;
    const dialog = ref.current;
    if (!dialog || matchMedia("(prefers-reduced-motion: reduce)").matches) {
      onClose();
      return;
    }
    const animation = dialog.animate(
      [
        { opacity: 1, transform: "translateY(0)" },
        {
          opacity: 0,
          transform:
            sheet && innerWidth <= 800 ? "translateY(48px)" : "translateY(8px)",
        },
      ],
      { duration: 140, easing: "ease-in", fill: "forwards" },
    );
    animation.onfinish = onClose;
  };
  useEffect(() => {
    const dialog = ref.current!;
    const previous =
      returnFocus?.current ||
      (document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null);
    dialog.showModal();
    if (sheet)
      dialog.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
    return () => {
      dialog.close();
      previous?.focus({ preventScroll: true });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className={sheet ? "task-sheet" : undefined}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        if (e.target === ref.current) close();
      }}
    >
      {sheet && (
        <button
          type="button"
          className="sheet-handle"
          aria-label="Dismiss sheet"
          disabled={!dismissible}
          onPointerDown={(e) => {
            if (!dismissible) return;
            drag.current = e.clientY;
            dragged.current = false;
            e.currentTarget.setPointerCapture(e.pointerId);
          }}
          onPointerMove={(e) => {
            if (drag.current !== null) {
              if (Math.abs(e.clientY - drag.current) > 8)
                dragged.current = true;
              ref.current!.style.transform = `translateY(${Math.max(0, e.clientY - drag.current)}px)`;
            }
          }}
          onPointerUp={(e) => {
            const distance =
              drag.current === null ? 0 : e.clientY - drag.current;
            drag.current = null;
            ref.current!.style.transform = "";
            if (distance > 72) close();
          }}
          onPointerCancel={() => {
            drag.current = null;
            ref.current!.style.transform = "";
          }}
          onClick={() => {
            if (!dragged.current) close();
          }}
        >
          <span />
        </button>
      )}
      <div className="modal-title">
        <h2 id={id} tabIndex={-1}>
          {title}
        </h2>
        <button
          className="icon-button"
          aria-label="Close dialog"
          disabled={!dismissible}
          onClick={close}
        >
          <X size={22} />
        </button>
      </div>
      {children}
    </dialog>
  );
}
export function Notice({
  children,
  type = "info",
}: {
  children: ReactNode;
  type?: "info" | "error" | "success";
}) {
  return (
    <div
      className={`notice ${type}`}
      role={type === "error" ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
